import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, Check, Copy, Link2, Mail } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { PillTabs } from '../../../components/design-system/admin';
import { InvitationDelivery } from '../../../common/hooks/use-invitations-api.hook';
import { useTeamApi } from '../../../common/hooks/use-team-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { isSupportRefusal } from '../../../common/services/support-session.service';

const MAX_USES_LIMIT = 50;
const KNOWN_ERRORS = ['EMAIL_TAKEN'];
/** Codes with a team-specific message. */
const TEAM_ERRORS = ['TOO_MANY_INVITATIONS', 'NO_SEATS_LEFT'];
const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

/**
 * Invite a teammate: they join as an admin of this account and choose their
 * own password. By email, or a link to pass on (shown once).
 */
export function TeamInviteDialog({ open, onOpenChange, onInvited, seatsLeft }: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onInvited: () => void;
    /** Seats the plan still has free; undefined = no limit. A link can't let in more people than that. */
    seatsLeft?: number;
}) {
    const { t } = useTranslation();
    const teamApi = useTeamApi();

    const [delivery, setDelivery] = useState<InvitationDelivery>('email');
    const [email, setEmail] = useState('');
    const [maxUses, setMaxUses] = useState('1');
    const [check, setCheck] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [link, setLink] = useState<{ url: string; maxUses: number } | null>(null);
    const [copied, setCopied] = useState(false);

    const uses = Number(maxUses);
    const emailOk = delivery !== 'email' || isValidEmail(email);
    const maxUsesAllowed = seatsLeft === undefined ? MAX_USES_LIMIT : Math.min(MAX_USES_LIMIT, seatsLeft);
    const usesOk = delivery !== 'link' || (Number.isInteger(uses) && uses >= 1 && uses <= maxUsesAllowed);
    const overSeats = seatsLeft !== undefined && Number.isInteger(uses) && uses > seatsLeft && uses <= MAX_USES_LIMIT;

    const reset = () => {
        setDelivery('email');
        setEmail('');
        setMaxUses('1');
        setCheck(false);
        setError(null);
        setLink(null);
        setCopied(false);
    };

    const close = (next: boolean) => {
        if (!next) reset();
        onOpenChange(next);
    };

    const submit = async () => {
        setCheck(true);
        setError(null);
        if (!emailOk || !usesOk || busy) return;
        setBusy(true);
        try {
            const res = await teamApi.invite(delivery === 'email' ? { delivery, email: email.trim() } : { delivery, maxUses: uses });
            onInvited();
            if (delivery === 'link') {
                setLink({ url: res.link, maxUses: res.invitation.maxUses });
                return;
            }
            if (res.emailSent) toast.success(t('ADMIN_USERS.INVITE.SENT', { email: res.invitation.email }));
            else if (res.emailError === 'not_configured') toast.error(t('ADMIN_USERS.INVITE.MAIL_NOT_CONFIGURED'));
            else toast.warning(t('ADMIN_USERS.INVITE.SENT_NO_EMAIL'));
            close(false);
        } catch (e) {
            const { code, left } = await readApiError(e);
            setError(
                // Ardealy support in the account: say why, not "try again".
                isSupportRefusal(code)
                    ? t(`SUPPORT_ACCESS.REFUSED.${code}`)
                    : code === 'NO_SEATS_LEFT' && left > 0
                    ? t('VENDOR_TEAM.SEATS_LEFT', { count: left })
                    : TEAM_ERRORS.includes(code)
                    ? t(`VENDOR_TEAM.ERRORS.${code}`)
                    : t(`ADMIN_USERS.INVITE.ERRORS.${KNOWN_ERRORS.includes(code) ? code : 'GENERIC'}`),
            );
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link.url);
            setCopied(true);
            toast.success(t('ADMIN_USERS.INVITE.COPIED'));
        } catch {
            toast.error(t('ADMIN_USERS.INVITE.COPY_FAILED'));
        }
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent data-testid="team-invite-dialog">
                <DialogHeader>
                    <DialogTitle>{link ? t('ADMIN_USERS.INVITE.LINK_TITLE') : t('VENDOR_TEAM.INVITE_TITLE')}</DialogTitle>
                    <DialogDescription>
                        {link ? t('ADMIN_USERS.INVITE.LINK_DESCRIPTION', { count: link.maxUses }) : t('VENDOR_TEAM.INVITE_DESCRIPTION')}
                    </DialogDescription>
                </DialogHeader>

                {link ? (
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-2 sm:flex-row">
                            <Input readOnly value={link.url} aria-label={t('ADMIN_USERS.INVITE.LINK_LABEL')} onFocus={(e) => e.target.select()} className="font-mono text-xs" />
                            <Button onClick={copy} className="shrink-0">
                                {copied ? <Check /> : <Copy />}
                                {copied ? t('ADMIN_USERS.INVITE.COPIED') : t('ADMIN_USERS.INVITE.COPY')}
                            </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">{t('ADMIN_USERS.INVITE.LINK_ONCE')}</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {error && (
                            <Alert variant="destructive" data-testid="team-invite-error">
                                <AlertCircle />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}
                        <PillTabs<InvitationDelivery>
                            value={delivery}
                            onChange={(value) => { setDelivery(value); setCheck(false); }}
                            ariaLabel={t('ADMIN_USERS.INVITE.DELIVERY_LABEL')}
                            options={[
                                { value: 'email', label: t('ADMIN_USERS.INVITE.DELIVERY_EMAIL'), icon: <Mail /> },
                                { value: 'link', label: t('ADMIN_USERS.INVITE.DELIVERY_LINK'), icon: <Link2 /> },
                            ]}
                        />
                        <p className="text-sm text-muted-foreground">
                            {t(delivery === 'email' ? 'ADMIN_USERS.INVITE.DELIVERY_EMAIL_HINT' : 'ADMIN_USERS.INVITE.DELIVERY_LINK_HINT')}
                        </p>
                        {delivery === 'email' ? (
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="team-invite-email" className="text-xs text-muted-foreground">{t('ADMIN_USERS.INVITE.EMAIL')} *</Label>
                                <Input
                                    id="team-invite-email"
                                    type="email"
                                    autoComplete="off"
                                    placeholder={t('ADMIN_USERS.INVITE.EMAIL_PLACEHOLDER')}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && submit()}
                                    aria-invalid={(check && !emailOk) || undefined}
                                />
                                {check && !emailOk && <p className="text-xs text-destructive">{t('ADMIN_USERS.VALIDATION.EMAIL_INVALID')}</p>}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1.5 sm:max-w-xs">
                                <Label htmlFor="team-invite-uses" className="text-xs text-muted-foreground">{t('ADMIN_USERS.INVITE.MAX_USES')}</Label>
                                <Input
                                    id="team-invite-uses"
                                    type="number"
                                    inputMode="numeric"
                                    min={1}
                                    max={maxUsesAllowed}
                                    step={1}
                                    value={maxUses}
                                    onChange={(e) => setMaxUses(e.target.value)}
                                    aria-invalid={(check && !usesOk) || undefined}
                                />
                                <p className={check && !usesOk ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
                                    {check && !usesOk
                                        ? overSeats ? t('VENDOR_TEAM.SEATS_LEFT', { count: seatsLeft }) : t('ADMIN_USERS.VALIDATION.MAX_USES')
                                        : seatsLeft !== undefined ? t('VENDOR_TEAM.SEATS_LEFT', { count: seatsLeft }) : t('ADMIN_USERS.INVITE.MAX_USES_HINT')}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    {link ? (
                        <Button variant="outline" onClick={() => close(false)}>{t('ADMIN_USERS.INVITE.DONE')}</Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => close(false)}>{t('ADMIN_SHELL.COMMON.CANCEL')}</Button>
                            <Button onClick={submit} disabled={busy}>
                                {delivery === 'email' ? <Mail /> : <Link2 />}
                                {delivery === 'email' ? t('ADMIN_USERS.INVITE.SEND') : t('ADMIN_USERS.INVITE.CREATE_LINK')}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
